import type {MutableRefObject, ReactElement} from 'react';
import React, {useRef} from 'react';
import {getAssetUrl} from '@Utils/s3.util';
import {Text, TextSize} from '@Components/text';
import {Button, Type} from '@Components/button';
import {noop} from '@Utils/general.util';
import {isIOSAppOrEmbeddedEditor} from '@Utils/browser.util';
import {deleteCookie, readCookie, setCookie} from '@Utils/cookie.util';
import {getStateFromUrl} from '@Utils/url.util';
import type {AjaxSuccessResponse} from '@Utils/ajax.util';
import {COOKIE_FB_SESSION_KEY} from '@Panels/user-media-panel/user-media-panel.types';
import styles from './connect-to-facebook.module.scss';

interface ConnectToFacebookProps {
  oauthUrl?: string;
  onLoggedIn(): void;
  onConnecting(): void;
}

const OAUTH_DATA_COOKIE = 'pmwsac';

export function ConnectToFacebook({oauthUrl = '', onLoggedIn = noop, onConnecting = noop}: ConnectToFacebookProps): ReactElement {
  const interval: MutableRefObject<NodeJS.Timeout | null> = useRef(null);

  const onConnectToFb = (response: AjaxSuccessResponse): void => {
    if (response.status === 'success' && response.data && response.data.fbKey) {
      setCookie(COOKIE_FB_SESSION_KEY, response.data.fbKey as string, 1);
      onLoggedIn();
    }
  };

  const checkLoggedInStatus = async (): Promise<void> => {
    const response = (await window.PMW.pollingAjaxCallAsync(
      async () => {},
      () => {
        return window.PMW.readLocal('authenticate/hasAccountBeenLoggedIn', {
          state: getStateFromUrl(oauthUrl ?? ''),
        });
      }
    )) as AjaxSuccessResponse;
    onConnectToFb(response);
  };

  const clearCookieInterval = (): void => {
    if (interval.current) {
      clearInterval(interval.current);
      deleteCookie(OAUTH_DATA_COOKIE);
    }
  };

  const onFacebookConnect = (): void => {
    window.open(oauthUrl, '_blank');
    onConnecting();
    if (isIOSAppOrEmbeddedEditor()) {
      void checkLoggedInStatus();
      return;
    }
    interval.current = setInterval(() => {
      const cookie = readCookie(OAUTH_DATA_COOKIE);
      if (cookie) {
        clearCookieInterval();
        const response = JSON.parse(decodeURIComponent(cookie)) as AjaxSuccessResponse;
        onConnectToFb(response);
      }
    }, 500);
  };

  return (
    <div className="flex-column-justify-center flex-items-center _full-height">
      <div className={`flex-center radius-round ${styles.fbLogoContainer}`}>
        <img src={getAssetUrl('facebook-logo-thumb-circle.png')} alt={window.i18next.t('pmwjs_add_from_facebook')} />
      </div>
      <Text className={`content-body spacing-p-t-4 spacing-p-b-4 ${styles.text}`} size={TextSize.SMALL} val={window.i18next.t('pmwjs_to_use_fb_photos_login')} />
      <Button type={Type.PRIMARY} customClasses="-noun" text={window.i18next.t('pmwjs_connect_to_facebook')} onClick={onFacebookConnect} isLoading={!oauthUrl} />
    </div>
  );
}
